import { DealListItemResponse, DealRequestType, getDealByID } from 'app/console-api';
import { FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import { DealEditFormAwaiting } from 'app/components/management/apps/deals/edit/deal-edit-form-awaiting';
import styled from 'styled-components';
import { DealEditFormGeneral } from 'app/components/management/apps/deals/edit/deal-edit-form-general';
import { AppCategory, getAppCategories } from 'app/console-api/apps/categories';
import { RewardsType } from 'app/console-api/apps/types';
import { DealStatus } from 'app/components/common/deals-statuses/deals-statuses';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { generatePath, useParams } from 'react-router-dom';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { LoadingContainer, PageContainer } from 'app/components/common/common-styles';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { ErrorResult } from 'app/organizations-api/types';
import { getRewardsTypes } from 'app/console-api/apps/rewards';
import { APPS_DEALS_VIEW_PATH, APPS_TASKS_VIEW_PATH, SIGN_IN_PATH } from 'app/route/paths';

const StyledSubtitleText = styled.div`
  font-size: 18px;
  font-weight: bold;
`;

const StyledFlexColumn = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    width: ['300px', '400px', '500px']
  })
);

export const DealEditPage = () => {
  const { appID, setBreadcrumbs } = useTabsCtx();
  const { id } = useParams<{ id: string }>();
  const [deal, setDeal] = React.useState<DealListItemResponse | undefined>(undefined);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);

  const [appCategories, setAppCategories] = useState<AppCategory[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [rewardsTypes, setRewardsTypes] = useState<RewardsType[]>([]);

  useEffect(() => {
    if (!id) {
      console.error('No ID provided in the route.');
      return;
    }

    getRewardsTypes(appID)
      .then((response) => setRewardsTypes(response.data || []))
      .catch((error: any) => {
        console.error('Failed to fetch rewards types', error);
      });

    getDealByID(appID, id)
      .then((response) => {
        setDeal(response);
        setBreadcrumbs([
          {
            label: response.dealDetails.title,
            to: generatePath(APPS_DEALS_VIEW_PATH, { appID, id: response.dealDetails.id })
          },
          {
            label: 'Edit '
          }
        ]);
        return () => setBreadcrumbs([]);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to fetch deal by id:', error);
      });
  }, [id]);

  useEffect(() => {
    if (!deal || !deal.myApp || !deal.myApp.id) {
      return;
    }
    getAppCategories(deal?.myApp.id)
      .then((response) => {
        const categories = response.data || [];
        setAppCategories(categories);
        if (!deal.categories || deal.categories.length === 0) {
          setCategoryIds([]);
          return;
        }
        const categoryIds = deal.categories
          .map((catName) => categories.find((c) => c.name === catName))
          .filter((c): c is AppCategory => !!c)
          .map((c) => c.id);
        setCategoryIds(categoryIds);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to load app categories:', error);
      });
  }, [deal]);

  if (!deal) {
    return (
      <FlexColumn style={{ width: '100%' }} gap={32}>
        <PageContainer>
          <LoadingContainer>
            <SkeletonLoader height={'600px'} />
          </LoadingContainer>
        </PageContainer>
      </FlexColumn>
    );
  }

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <PageContainer>
      <FlexRow justify={'center'} align={'center'}>
        <StyledFlexColumn gap={32}>
          <StyledSubtitleText>Settings</StyledSubtitleText>
          {deal.dealStatus === DealStatus.Pending &&
          deal.dealDetails.requestType === DealRequestType.Incoming ? (
            <DealEditFormAwaiting
              data={deal}
              categoryIds={categoryIds}
              appCategories={appCategories}
              rewardsTypes={rewardsTypes}
            />
          ) : (
            <DealEditFormGeneral
              data={deal}
              categoryIds={categoryIds}
              appCategories={appCategories}
              rewardsTypes={rewardsTypes}
            />
          )}
        </StyledFlexColumn>
      </FlexRow>
    </PageContainer>
  );
};
